import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Stepper, Step, StepLabel,
  Card, CardContent, Grid, RadioGroup, Radio, FormControlLabel,
  Alert, CircularProgress, Chip, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, Checkbox,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { CheckCircle, School, CalendarMonth, Lock } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import {
  API_URL, STRIPE_KEY,
  HOME_ASSESSMENT_PRICE, BUSINESS_ASSESSMENT_PRICE,
} from '../utils/constants';

const stripePromise = loadStripe(STRIPE_KEY);

const steps = ['Your Information', 'Choose College', 'Schedule', 'Review & Pay'];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND',
  'OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

import { getActiveColleges } from '../data/colleges';
import CollegeLogo from './common/CollegeLogo';

const anyOption = { id: 'any', name: 'Any Available Intern', abbreviation: 'ANY', location: 'Fastest scheduling', color: '#9ca3af' };
const defaultColleges = [anyOption, ...getActiveColleges()];

const timeSlots = [
  { id: 'morning', label: 'Morning', time: '9:00 AM - 11:00 AM' },
  { id: 'midday', label: 'Midday', time: '11:00 AM - 1:00 PM' },
  { id: 'afternoon', label: 'Afternoon', time: '1:00 PM - 3:00 PM' },
  { id: 'late', label: 'Late Afternoon', time: '3:00 PM - 5:00 PM' },
];

const validationSchemas = [
  Yup.object({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    phone: Yup.string().required('Phone number is required'),
    business_name: Yup.string(),
    street: Yup.string().required('Street address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required').matches(/^[A-Za-z]{2}$/, 'Use 2-letter state code (e.g. AZ)'),
    zip: Yup.string().required('ZIP code is required').matches(/^\d{5}(-\d{4})?$/, 'Enter a valid 5-digit ZIP'),
    sms_consent: Yup.boolean(), // optional checkbox
  }),
  Yup.object({ college: Yup.string().required('Please select a college') }),
  Yup.object({
    date: Yup.string().required('Please select a date'),
    time_slot: Yup.string().required('Please select a time slot'),
  }),
  Yup.object({ referral_code: Yup.string() }),
];

function BookingFormInner({ colleges, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [assessmentType, setAssessmentType] = useState('home');
  // When the backend returns 409 {error: 'existing_booking', ...} we show a
  // friendly info banner with deep-links instead of the generic red error.
  const [existingBooking, setExistingBooking] = useState(null);
  // B3: per-date slot availability fetched from /booking/get_available_slots.
  // Map of slot_id -> {available: bool, intern_count: number}. While the
  // request is in-flight (or the user hasn't picked a date), all slots are
  // treated as enabled so the UI doesn't flicker / mis-disable on first load.
  const [slotAvailability, setSlotAvailability] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const collegeList = (colleges && colleges.length > 0) ? [defaultColleges[0], ...colleges] : defaultColleges;

  const homePrice = HOME_ASSESSMENT_PRICE;
  const businessPrice = BUSINESS_ASSESSMENT_PRICE;
  const currentPrice = assessmentType === 'business' ? businessPrice : homePrice;
  const priceDisplay = `$${currentPrice.toFixed(2)}`;

  const formik = useFormik({
    initialValues: {
      name: '', email: '', phone: '', business_name: '',
      street: '', city: '', state: '', zip: '',
      college: 'any',
      date: '', time_slot: '',
      referral_code: '',
      sms_consent: false,
    },
    validationSchema: validationSchemas[activeStep],
    onSubmit: async (values) => {
      if (activeStep < steps.length - 1) {
        setActiveStep(prev => prev + 1);
      } else {
        if (!stripe || !elements) {
          setError('Payment system is loading. Please wait a moment and try again.');
          return;
        }

        setSubmitting(true);
        setError('');
        try {
          // Step 1: Create booking
          const bookingData = {
            action: 'create_booking',
            client_info: {
              name: values.name,
              email: values.email,
              phone: values.phone,
              business_name: values.business_name,
              address: {
                street: values.street,
                city: values.city,
                state: values.state.toUpperCase(),
                zip: values.zip,
              },
              sms_consent: !!values.sms_consent,
              sms_consent_at: values.sms_consent ? new Date().toISOString() : null,
              sms_consent_text: values.sms_consent
                ? 'I agree to receive transactional SMS from Cyber Wise Corp about my cybersecurity assessment booking, including confirmations, reminders, and schedule changes. Msg & data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel.'
                : null,
            },
            preferred_college: values.college === 'any' ? null : values.college,
            requested_time: (() => {
              // B7 v2: prefer the per-slot ISO the backend already resolved
              // for the client's local timezone (DST-aware via zoneinfo).
              // That's authoritative -- the same value the backend used to
              // run availability matching when it filled the slot board.
              // Fall back to a Phoenix-anchored local string only if the
              // slot-availability fetch hasn't populated yet OR if it
              // failed (e.g. user submitted with an offline backend).
              const slotMeta = slotAvailability && slotAvailability[values.time_slot];
              if (slotMeta && slotMeta.iso) return slotMeta.iso;
              const hourMap = { morning: '09:00', midday: '11:00', afternoon: '13:00', late: '15:00' };
              const time24 = hourMap[values.time_slot] || '09:00';
              return `${values.date}T${time24}:00-07:00`;
            })(),
            referral_code: values.referral_code || null,
          };
          const bookingRes = await fetch(`${API_URL}/booking/create_booking`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bookingData),
          });
          const bookingResult = await bookingRes.json();
          if (!bookingRes.ok) {
            // 409 with error='existing_booking': the client already has an
            // active booking at this time. Show a friendly info banner with
            // deep-links instead of the red error.
            if (bookingRes.status === 409 && bookingResult.error === 'existing_booking') {
              setError('');
              setExistingBooking({
                booking_id: bookingResult.existing_booking_id || '',
                status_url: bookingResult.booking_status_url || '/booking-status',
                message: bookingResult.message || 'It looks like you already have an appointment scheduled for this time.',
              });
              setSubmitting(false);
              return;
            }
            setError(bookingResult.error || 'Booking failed.');
            setSubmitting(false);
            return;
          }

          // Step 2: Create PaymentIntent
          const paymentRes = await fetch(`${API_URL}/payment/create_payment_intent`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'create_payment_intent',
              assessment_type: assessmentType,
              client_email: values.email,
              client_name: values.name,
              booking_id: bookingResult.booking_id || '',
            }),
          });
          const paymentResult = await paymentRes.json();
          if (!paymentRes.ok) {
            setError(paymentResult.error || 'Payment setup failed.');
            setSubmitting(false);
            return;
          }

          // Step 3: Confirm payment with Stripe
          const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
            paymentResult.client_secret,
            {
              payment_method: {
                card: elements.getElement(CardElement),
                billing_details: { name: values.name, email: values.email },
              },
            }
          );

          if (stripeError) {
            setError(stripeError.message);
            setSubmitting(false);
            return;
          }

          if (paymentIntent.status === 'succeeded') {
            // Update payment status in DynamoDB
            try {
              await fetch(`${API_URL}/payment/update_status`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action: 'update_status',
                  payment_id: paymentResult.payment_id,
                  status: 'succeeded',
                }),
              });
            } catch (e) {
              console.warn('Could not update payment status:', e);
            }

            setSuccess({
              ...bookingResult,
              payment_status: 'succeeded',
              amount: paymentResult.amount_display,
            });
          } else {
            setError('Payment was not completed. Please try again.');
          }
        } catch (err) {
          console.error('Booking error:', err);
          setError(err.message || 'Something went wrong. Please try again.');
        }
        setSubmitting(false);
      }
    },
  });

  const handleBack = () => setActiveStep(prev => prev - 1);

  const getSelectedCollege = () => collegeList.find(c => c.id === formik.values.college);
  const getSelectedTime = () => timeSlots.find(t => t.id === formik.values.time_slot);

  // B3: when the date changes, ask the backend which of the 4 time slots
  // actually has an intern available. Slots with 0 eligible interns get
  // disabled so the user can't pick a slot we can't fulfill.
  const formikDate = formik.values.date;
  const formikCollege = formik.values.college;
  const formikZip = formik.values.zip;
  const formikState = formik.values.state;
  useEffect(() => {
    if (!formikDate) {
      setSlotAvailability(null);
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    fetch(`${API_URL}/booking/get_available_slots`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'get_available_slots',
        date: formikDate,
        preferred_college: formikCollege && formikCollege !== 'any' ? formikCollege : null,
        client_address: formikZip
          ? { zip: formikZip, state: (formikState || '').toUpperCase() }
          : null,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data.available_slots || []).forEach((s) => {
          // B7 v2: store the per-slot ISO the backend resolved for the
          // client's local timezone (DST-aware). Frontend uses it verbatim
          // at submit instead of computing one with the browser's TZ.
          map[s.id] = {
            available: !!s.available,
            intern_count: s.intern_count || 0,
            iso: s.iso || null,
          };
        });
        setSlotAvailability(map);
      })
      .catch((e) => {
        if (cancelled) return;
        // On error, fall back to "all slots enabled" so the form still works.
        console.warn('get_available_slots failed; showing all slots enabled:', e);
        setSlotAvailability(null);
      })
      .finally(() => {
        if (!cancelled) setSlotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formikDate, formikCollege, formikZip, formikState]);

  // Clear time_slot if the picked slot becomes unavailable after a date change.
  useEffect(() => {
    if (!slotAvailability || !formik.values.time_slot) return;
    const meta = slotAvailability[formik.values.time_slot];
    if (meta && !meta.available) {
      formik.setFieldValue('time_slot', '');
    }
  }, [slotAvailability]); // formik intentionally excluded -- only react to availability changes

  // Earliest booking date is tomorrow (interns need lead time to accept).
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      {existingBooking && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          onClose={() => setExistingBooking(null)}
        >
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {existingBooking.message}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                href={
                  existingBooking.booking_id
                    ? `${existingBooking.status_url}?id=${encodeURIComponent(existingBooking.booking_id)}`
                    : existingBooking.status_url
                }
              >
                View My Booking
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => {
                  setExistingBooking(null);
                  setActiveStep(2);
                  if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                Choose a Different Time
              </Button>
            </Box>
          </Box>
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      <form onSubmit={formik.handleSubmit}>
        {/* Step 1: Your Information */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Tell Us About Yourself
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              For homeowners and small businesses ready to understand their security posture.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" name="name" required
                  value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email Address" name="email" type="email" required
                  value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone Number" name="phone" required
                  value={formik.values.phone} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Business Name (optional)" name="business_name"
                  value={formik.values.business_name} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  Service Address
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Where the assessment will take place. We use the ZIP to match you with a nearby intern.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Street Address" name="street" required
                  value={formik.values.street} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.street && Boolean(formik.errors.street)}
                  helperText={formik.touched.street && formik.errors.street} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="City" name="city" required
                  value={formik.values.city} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth required error={formik.touched.state && Boolean(formik.errors.state)}>
                  <InputLabel>State</InputLabel>
                  <Select name="state" label="State" value={formik.values.state} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                    {US_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="ZIP" name="zip" required inputProps={{ maxLength: 10 }}
                  value={formik.values.zip} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.zip && Boolean(formik.errors.zip)}
                  helperText={formik.touched.zip && formik.errors.zip} />
              </Grid>

              {/* SMS opt-in — explicit, unchecked by default. Required by carriers
                  (TCR / A2P 10DLC) for any campaign that sends transactional SMS. */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: '#fafbff' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="sms_consent"
                        checked={formik.values.sms_consent}
                        onChange={formik.handleChange}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        <strong>SMS notifications (optional).</strong>{' '}
                        I agree to receive transactional SMS from Cyber Wise Corp about my cybersecurity assessment booking, including <em>confirmations, reminders, and schedule changes</em>. Msg &amp; data rates may apply. Msg frequency varies. Reply <strong>HELP</strong> for help, <strong>STOP</strong> to cancel. See our{' '}
                        <a href="/privacy" style={{ color: '#7c3aed' }}>Privacy Policy</a> and{' '}
                        <a href="/terms#sms" style={{ color: '#7c3aed' }}>SMS Terms</a>.
                      </Typography>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                    Leaving this unchecked is fine — you&apos;ll still receive booking confirmations by email.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Step 2: Choose College */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Choose a College to Support
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your assessment fee directly supports cybersecurity students at your chosen institution.
            </Typography>
            <RadioGroup name="college" value={formik.values.college} onChange={formik.handleChange}>
              {collegeList.map(college => (
                <Card key={college.id} sx={{ mb: 2, border: formik.values.college === college.id ? '2px solid #7c3aed' : '2px solid transparent',
                  cursor: 'pointer', transition: 'border-color 0.2s' }}
                  onClick={() => formik.setFieldValue('college', college.id)}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                    <FormControlLabel value={college.id} control={<Radio />} label="" sx={{ mr: 0 }} />
                    <Box sx={{ mr: 2, width: 50 }}><CollegeLogo college={college} height={40} /></Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{college.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{college.location}</Typography>
                    </Box>
                    {college.active_interns > 0 && (
                      <Chip label={`${college.active_interns} interns`} size="small" color="primary" variant="outlined" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>
          </Box>
        )}

        {/* Step 3: Schedule */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Schedule Your Assessment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Assessments are available Monday through Saturday.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Preferred Date" name="date" type="date"
                  InputLabelProps={{ shrink: true }} inputProps={{ min: minDate }}
                  value={formik.values.date} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.date && Boolean(formik.errors.date)}
                  helperText={formik.touched.date && formik.errors.date} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Preferred Time
                  {slotsLoading && (
                    <CircularProgress size={14} sx={{ ml: 1, verticalAlign: 'middle' }} />
                  )}
                </Typography>
                {!formikDate && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Pick a date above to see available times.
                  </Typography>
                )}
                <Grid container spacing={2}>
                  {timeSlots.map(slot => {
                    // Default to "enabled" if we don't have availability info
                    // (no date yet, or backend lookup failed). If we DO have
                    // availability info, disable slots with 0 eligible interns.
                    const meta = slotAvailability ? slotAvailability[slot.id] : null;
                    const disabled = meta ? !meta.available : false;
                    const selected = formik.values.time_slot === slot.id;
                    return (
                      <Grid item xs={6} sm={3} key={slot.id}>
                        <Card sx={{
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          textAlign: 'center', py: 2,
                          opacity: disabled ? 0.4 : 1,
                          border: selected ? '2px solid #7c3aed' : '2px solid transparent',
                          backgroundColor: selected ? 'rgba(124,58,237,0.05)' : 'white',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: disabled ? 'transparent' : '#7c3aed' },
                        }}
                          onClick={() => !disabled && formik.setFieldValue('time_slot', slot.id)}>
                          <CardContent sx={{ p: '12px !important' }}>
                            <CalendarMonth sx={{ color: selected ? '#7c3aed' : 'grey.400', mb: 0.5 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{slot.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{slot.time}</Typography>
                            {disabled && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'error.main', fontWeight: 600 }}>
                                Unavailable
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                {formik.touched.time_slot && formik.errors.time_slot && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>{formik.errors.time_slot}</Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Step 4: Review & Book */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Review Your Booking</Typography>
            <Card sx={{ mb: 3, backgroundColor: 'grey.50' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{formik.values.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{formik.values.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{formik.values.phone}</Typography>
                  </Grid>
                  {formik.values.business_name && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Business</Typography>
                      <Typography variant="body1">{formik.values.business_name}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Service Address</Typography>
                    <Typography variant="body1">
                      {formik.values.street}, {formik.values.city}, {formik.values.state.toUpperCase()} {formik.values.zip}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}><Divider /></Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">College</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{getSelectedCollege()?.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Date & Time</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formik.values.date} - {getSelectedTime()?.time}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Assessment Type */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Assessment Type</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { type: 'home', label: 'Home Assessment', price: homePrice, desc: '1-hour on-site' },
                { type: 'business', label: 'Business Assessment', price: businessPrice, desc: '2-hour on-site, 10 or fewer employees' },
              ].map(opt => (
                <Grid item xs={12} sm={6} key={opt.type}>
                  <Card sx={{ cursor: 'pointer', p: 2, textAlign: 'center',
                    border: assessmentType === opt.type ? '2px solid #7c3aed' : '2px solid transparent',
                    backgroundColor: assessmentType === opt.type ? 'rgba(124,58,237,0.05)' : 'white' }}
                    onClick={() => setAssessmentType(opt.type)}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{opt.label}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#7c3aed' }}>
                      ${opt.price.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{opt.desc}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <TextField fullWidth label="Referral Code (optional)" name="referral_code" sx={{ mb: 3 }}
              value={formik.values.referral_code} onChange={formik.handleChange}
              helperText="Have a referral code? Your intern earns a $10 bonus when you use their code!" />

            {/* Payment */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lock sx={{ fontSize: 18 }} /> Payment Information
            </Typography>
            <Card sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0' }}>
              <CardElement options={{
                style: {
                  base: { fontSize: '16px', color: '#1e1e1e', '::placeholder': { color: '#9ca3af' } },
                  invalid: { color: '#dc2626' },
                },
              }} />
            </Card>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Secured by Stripe. Your card details are encrypted and never stored on our servers.
            </Typography>

            <Card sx={{ mb: 3, border: '2px solid', borderImage: 'linear-gradient(135deg, #2563eb, #7c3aed) 1' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">{assessmentType === 'business' ? 'Business' : 'Home'} Security Assessment</Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1">{priceDisplay}</Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {assessmentType === 'business' ? '2-hour' : '1-hour'} assessment + consultation + expert review + security report
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#7c3aed' }}>{priceDisplay}</Typography>
                </Box>
              </CardContent>
            </Card>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              By booking, you agree to our Terms of Service. Cancellation is free with 24 hours notice.
            </Typography>
          </Box>
        )}

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined" sx={{ px: 3 }}>
            Back
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}
            sx={{ px: 4, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', fontWeight: 600,
              '&:hover': { background: 'linear-gradient(135deg, #1d4ed8, #6d28d9)' } }}>
            {submitting ? <CircularProgress size={24} color="inherit" /> :
              activeStep === steps.length - 1 ? `Pay ${priceDisplay} and Book` : 'Next'}
          </Button>
        </Box>
      </form>

      {/* Success Dialog */}
      <Dialog open={Boolean(success)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Assessment Booked!</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your cybersecurity assessment has been submitted. We're matching you with an available intern now.
          </Typography>
          {success && (
            <Box sx={{ backgroundColor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Booking ID</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{success.booking_id}</Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            You'll receive a confirmation email at {formik.values.email} once an intern accepts your booking.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" onClick={() => { setSuccess(null); onSuccess?.(); }}
            sx={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Wrap with Stripe Elements provider
function BookingForm({ colleges, onSuccess }) {
  return (
    <Elements stripe={stripePromise}>
      <BookingFormInner colleges={colleges} onSuccess={onSuccess} />
    </Elements>
  );
}

export default BookingForm;
